import * as mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import type { TranslateService } from '@ngx-translate/core';
import type { Feature, FeatureCollection, Point, Polygon } from 'geojson';
import { MapToolsControl } from './map-tools.control';
import { buildToolsLabels } from './map-tools-labels';
import { buildMapList } from './map-polygon-list';
import { openCoordinatesView } from './map-coordinates-view';
import type { WhispFeatureLookup } from './whisp-feature-findings';
import { createWhispFeatureDetail } from './whisp-feature-detail';

const SELECTED_SOURCE = 'map-list-selected-src';
const SELECTED_LAYER = 'map-list-selected';
const SELECTED_COLOR = '#ffd600';

export type SiteListFeature = Feature<Point | Polygon> & { id: string };

export interface MapFeatureListOptions {
  whisp?: WhispFeatureLookup | null;
  selectGroupId?: number;
}

export function siteListFeatures(collection: FeatureCollection | undefined): SiteListFeature[] {
  return (collection?.features ?? []).flatMap((feature, index) =>
    feature.geometry?.type === 'Polygon' || feature.geometry?.type === 'Point'
      ? [{ ...(feature as Feature<Point | Polygon>), id: String(feature.id ?? `feature-${index}`) }]
      : []
  );
}

export class MapFeatureListController {
  private selectedIds = new Set<string>();
  private popup?: mapboxgl.Popup;
  private readonly control: MapToolsControl;

  constructor(
    private readonly map: mapboxgl.Map,
    private readonly features: SiteListFeature[],
    private readonly translate: Pick<TranslateService, 'instant'>,
    private readonly options: MapFeatureListOptions = {}
  ) {
    const { areasLabel, listLabels } = buildToolsLabels(translate);
    this.control = new MapToolsControl({
      readOnly: true,
      areasLabel,
      listLabels,
      editLabel: translate.instant('Map_show_coordinates_tr'),
      getState: () => ({ isDrawing: false, isEditing: false, hasPolygon: this.features.length > 0 }),
      getPolygonList: () => this.items(),
      focusZoomEdit: (id) => this.focus(id),
      focusZoomEditAndShowCoords: (id) => {
        this.focus(id);
        this.showCoordinates(id);
      },
    });
  }

  attach(): void {
    if (!this.features.length) return;
    this.map.addControl(this.control, 'top-left');
    this.map.addSource(SELECTED_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    this.map.addLayer({
      id: SELECTED_LAYER,
      type: 'line',
      source: SELECTED_SOURCE,
      paint: { 'line-color': SELECTED_COLOR, 'line-width': 4 },
    });
    this.control.syncListHeightToMap(this.map);
    this.selectGroup();
  }

  private selectGroup(): void {
    const { whisp, selectGroupId } = this.options;
    if (!whisp || selectGroupId === undefined) return;
    const members = this.features.filter((feature) => whisp(feature)?.groupIds.includes(selectGroupId));
    if (!members.length) return;
    this.select(members);
    this.control.scrollToPolygon(members[0].id);
  }

  private select(features: SiteListFeature[]): void {
    this.selectedIds = new Set(features.map((feature) => feature.id));
    (this.map.getSource(SELECTED_SOURCE) as mapboxgl.GeoJSONSource | undefined)?.setData({
      type: 'FeatureCollection',
      features: features.filter((feature) => this.isPolygon(feature)),
    });
    this.control.refresh();
  }

  private items() {
    return buildMapList({
      polygons: this.features.filter((feature) => this.isPolygon(feature)),
      points: this.features.filter((feature) => !this.isPolygon(feature)),
      selectedIds: this.selectedIds,
      errorIds: () => false,
      whisp: this.options.whisp,
      areaLabel: this.translate.instant('Map_area_tr'),
      pointLabel: this.translate.instant('Map_point_tr'),
    });
  }

  private focus(id: string): void {
    const feature = this.feature(id);
    if (!feature) return;
    this.select([feature]);
    if (feature.geometry.type === 'Point')
      this.map.flyTo({
        center: feature.geometry.coordinates as [number, number],
        zoom: Math.max(this.map.getZoom(), 15),
      });
    else {
      const [west, south, east, north] = turf.bbox(feature);
      this.map.fitBounds(
        [
          [west, south],
          [east, north],
        ],
        { padding: 60, maxZoom: 17 }
      );
    }
  }

  private showCoordinates(id: string): void {
    const feature = this.feature(id);
    if (!feature) return;
    this.popup?.remove();
    const title = this.items().find((item) => item.id === id)?.label ?? '';
    const finding = this.options.whisp?.(feature);
    const detail = finding ? createWhispFeatureDetail(finding, this.translate) : undefined;
    this.popup = openCoordinatesView(this.map, feature, title, this.translate, detail);
  }

  private feature(id: string): SiteListFeature | undefined {
    return this.features.find((feature) => feature.id === id);
  }

  private isPolygon(feature: SiteListFeature): feature is SiteListFeature & Feature<Polygon> {
    return feature.geometry.type === 'Polygon';
  }
}
